import { useEffect, useRef, lazy, Suspense, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Lenis from 'lenis';
import { gsap } from 'gsap/gsap-core';
import { CSSPlugin } from 'gsap/CSSPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import Destinations from './components/Destinations';
import DeferredSection from './components/DeferredSection';
import SEO from './components/SEO';
import Preloader from './components/Preloader';
import { wasPrerendered } from './lib/prerender';
import { destinations } from './data/destinations';
import {
  About,
  CorporateTravel,
  DestinationPage,
  Explore,
  KnowledgeArticle,
  KnowledgeHub,
  NotFound,
  Privacy,
  TravelIntelligence,
} from './pages/routes';

// --------------------------------------------------------------------------
// Lazy-loaded sections and routes — code loads only when needed
// --------------------------------------------------------------------------
const Vision = lazy(() => import('./components/Vision'));
const RouteField = lazy(() => import('./components/RouteField'));
const WhatIsVoyara = lazy(() => import('./components/WhatIsVoyara'));
const Technology = lazy(() => import('./components/Technology'));
const Waitlist = lazy(() => import('./components/Waitlist'));


// Home links straight to the city guides so they sit one click from the
// site's strongest page instead of only behind /explore.
const FEATURED_DESTINATIONS = destinations.filter((destination) => destination.category === 'city');

// Register GSAP plugins ONCE with tree-shaking pattern
gsap.registerPlugin(CSSPlugin, ScrollTrigger);

// --------------------------------------------------------------------------
// Scroll to top on route change
// --------------------------------------------------------------------------
// Re-aim at a hash target for a short while after navigation (see below).
const HASH_RETRY_DELAYS_MS = [150, 400, 800, 1600, 2400];

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  
  useEffect(() => {
    // Disable browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    if (hash) {
      const id = hash.replace('#', '');
      
      const scrollToElement = () => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 100;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          return true;
        }
        return false;
      };

      // Aim now, then re-aim: the target may not exist yet, and on the home
      // page the deferred sections above it swap their placeholders for
      // content of a different height as their chunks arrive. Stop as soon
      // as the visitor scrolls on their own.
      scrollToElement();
      const timers = HASH_RETRY_DELAYS_MS.map((delay) => setTimeout(scrollToElement, delay));
      const stopRetrying = () => timers.forEach(clearTimeout);
      const userScrollEvents = ['wheel', 'touchstart', 'keydown'] as const;
      userScrollEvents.forEach((type) => window.addEventListener(type, stopRetrying, { once: true, passive: true }));
      return () => {
        stopRetrying();
        userScrollEvents.forEach((type) => window.removeEventListener(type, stopRetrying));
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  
  return null;
}

// --------------------------------------------------------------------------
// Deferred Component wrapper to load non-critical components after initial paint
// --------------------------------------------------------------------------
function DeferredComponent({ children }: { children: React.ReactNode }) {
  const [shouldRender, setShouldRender] = useState(false);
  
  useEffect(() => {
    // Defer past first paint so the hero renders instantly, but bring the
    // signature background in promptly after — a long delay leaves the hero
    // sitting on flat black. Prefer requestIdleCallback where available.
    const start = () => setShouldRender(true);
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (ric) {
      const id = ric(start, { timeout: 800 });
      return () => (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(id);
    }
    const timer = setTimeout(start, 600);
    return () => clearTimeout(timer);
  }, []);

  if (!shouldRender) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

// --------------------------------------------------------------------------
// Inline fallback for Suspense — minimal, on-brand loading indicator
// --------------------------------------------------------------------------
function SectionFallback() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="w-4 h-4 rounded-full border-2 border-white/10 border-t-white/50 animate-spin" />
    </div>
  );
}

// --------------------------------------------------------------------------
// Landing Page Component
// --------------------------------------------------------------------------
function LandingPage() {
  // A link to /#technology or /#waitlist mounts every section at once so
  // their chunks load straight away; ScrollToTop re-aims while they replace
  // their placeholders.
  const { hash } = useLocation();
  const isHashTarget = hash.length > 1;

  // ── SEO Schema ────────────────────────────────────────────────────────
  const fullSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://www.eclipsegroup.in/#organization",
        "name": "Eclipse Group",
        "legalName": "Eclipse Private Limited",
        "alternateName": ["Eclipse Media", "Eclipse Private Limited", "Eclipse"],
        "url": "https://www.eclipsegroup.in",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.eclipsegroup.in/favicon-512.png",
          "width": 512,
          "height": 512
        },
        "description": "Eclipse Group (operating as Eclipse Private Limited and Eclipse Media) develops intelligent technology infrastructure focused on coordination, automation, and experiential systems. Creator of Voyara AI and WavesFlo.",
        "brand": [
          { "@type": "Brand", "name": "Voyara AI" },
          { "@type": "Brand", "name": "WavesFlo", "url": "https://www.wavesflo.com" }
        ]
      },
      // Voyara AI is in private early access, with no public price and no
      // released app. So no Offer (a price of "0" read as free to use) and no
      // operatingSystem (it listed mobile apps that do not exist); the
      // description says how it is available, in the words /about uses.
      {
        "@type": "SoftwareApplication",
        "@id": "https://www.eclipsegroup.in/#software",
        "name": "Voyara AI",
        "applicationCategory": "TravelApplication",
        "description": "A travel-coordination platform built to keep flights, hotels, ground transport and schedules in step as a trip changes. It is in private early access, by request.",
        "author": { "@id": "https://www.eclipsegroup.in/#organization" }
      },
      {
        "@type": "WebSite",
        "@id": "https://www.eclipsegroup.in/#website",
        "url": "https://www.eclipsegroup.in",
        "name": "Voyara AI — Intelligent Travel Systems",
        "publisher": { "@id": "https://www.eclipsegroup.in/#organization" }
      }
    ]
  }), []);

  return (
    <>
      <SEO schema={fullSchema} />
      <main className="relative z-10">
        <section id="home">
          <Hero />
        </section>

        {/* Placeholder heights match each section's rendered height
            (measured from 360 to 1920 px wide). */}
        <DeferredSection
          id="vision"
          eager={isHashTarget}
          placeholderClassName="min-h-[600px] sm:min-h-[500px] lg:min-h-[560px] xl:min-h-[640px]"
        >
          <Vision />
        </DeferredSection>

        <DeferredSection
          id="technology"
          eager={isHashTarget}
          placeholderClassName="min-h-[4300px] sm:min-h-[2900px] md:min-h-[2450px] lg:min-h-[1950px]"
        >
          <WhatIsVoyara />
          <Technology />
        </DeferredSection>

        <DeferredSection
          id="waitlist"
          eager={isHashTarget}
          placeholderClassName="min-h-[1000px] sm:min-h-[900px] lg:min-h-[1000px]"
        >
          <Waitlist />
        </DeferredSection>

        <Destinations
          items={FEATURED_DESTINATIONS}
          header={
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <h2 className="font-serif tracking-tight text-[clamp(2.25rem,4.5vw,3.5rem)] leading-none mb-5">
                  Destination <span className="italic text-primary/70">guides.</span>
                </h2>
                <p className="text-secondary leading-relaxed">
                  How to get in, how to get around, and what Voyara AI keeps in step on a trip to each city.
                </p>
              </div>
              <Link
                to="/explore"
                className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors whitespace-nowrap"
              >
                All destinations and airports
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          }
        />
      </main>
    </>
  );
}

// --------------------------------------------------------------------------
// The signature RouteField background only runs behind the cinematic landing —
// skip the always-on canvas + RAF loop on content pages (knowledge, destinations, etc.)
// --------------------------------------------------------------------------
function RouteFieldGate() {
  const { pathname } = useLocation();
  if (pathname !== '/') return null;

  return (
    <DeferredComponent>
      <RouteField />
    </DeferredComponent>
  );
}

// --------------------------------------------------------------------------
// App Component
// --------------------------------------------------------------------------
function App() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorDotRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);

  // ── Lenis smooth scroll + GSAP sync ──────────────────────────────────
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.8,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 1.5,
    });

    lenis.on('scroll', ScrollTrigger.update);

    const updateLenis = (time: number) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(updateLenis);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(updateLenis);
      lenis.destroy();
    };
  }, []);

  // ── Custom cursor (refs + RAF — zero re-renders) ─────────────────────
  useEffect(() => {
    const cursor = cursorRef.current;
    const dot = cursorDotRef.current;
    const spotlight = spotlightRef.current;
    if (!cursor || !dot) return;

    // Check if this is a touch device
    const isTouchDevice = window.matchMedia('(hover: none)').matches;
    if (isTouchDevice) return;

    let mouseX = -100;
    let mouseY = -100;
    let cursorX = -100;
    let cursorY = -100;
    let rafId: number;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.left = `${mouseX}px`;
      dot.style.top = `${mouseY}px`;
      if (spotlight) {
        spotlight.style.left = `${mouseX}px`;
        spotlight.style.top = `${mouseY}px`;
        spotlight.style.opacity = '1';
      }
    };

    const handleMouseEnterInteractive = () => {
      cursor.style.width = '56px';
      cursor.style.height = '56px';
      cursor.style.borderColor = 'rgba(120, 140, 255, 0.12)';
      cursor.style.background = 'rgba(120, 140, 255, 0.02)';
    };

    const handleMouseLeaveInteractive = () => {
      cursor.style.width = '36px';
      cursor.style.height = '36px';
      cursor.style.borderColor = 'rgba(245, 245, 245, 0.04)';
      cursor.style.background = 'transparent';
    };

    const animate = () => {
      cursorX += (mouseX - cursorX) * 0.08;
      cursorY += (mouseY - cursorY) * 0.08;
      cursor.style.left = `${cursorX}px`;
      cursor.style.top = `${cursorY}px`;

      rafId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    rafId = requestAnimationFrame(animate);

    // Bind interactive hover effects using event delegation (more efficient)
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const interactive = target.closest('a, button, input, [data-magnetic]');
      if (interactive) {
        handleMouseEnterInteractive();
      } else {
        handleMouseLeaveInteractive();
      }
    };

    window.addEventListener('mouseover', handleMouseOver);

    const handleMouseLeaveWindow = () => {
      if (spotlight) spotlight.style.opacity = '0';
    };
    document.addEventListener('mouseleave', handleMouseLeaveWindow);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseleave', handleMouseLeaveWindow);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <a href="#main-content" className="skip-link">Skip to content</a>
        {/* A pre-rendered page is already on screen; covering it with the
            Preloader only blanks visible content and delays LCP. */}
        {!wasPrerendered && <Preloader />}
        <ScrollToTop />
        <div className="relative bg-black min-h-screen overflow-x-hidden">
          {/* ── Custom cursor system (desktop only) ── */}
          <div
            ref={cursorRef}
            className="fixed w-9 h-9 rounded-full border pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 hidden lg:block"
            style={{
              borderColor: 'rgba(245, 245, 245, 0.04)',
              transition: 'width 0.4s cubic-bezier(0.22,1,0.36,1), height 0.4s cubic-bezier(0.22,1,0.36,1), border-color 0.4s, background 0.4s',
              mixBlendMode: 'difference',
            }}
          />
          <div
            ref={cursorDotRef}
            className="fixed w-1 h-1 rounded-full bg-[#f5f5f5]/40 pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 hidden lg:block"
          />

          {/* ── Cursor-following ambient spotlight (desktop only) ── */}
          <div
            ref={spotlightRef}
            className="fixed w-[600px] h-[600px] rounded-full pointer-events-none z-[1] -translate-x-1/2 -translate-y-1/2 hidden lg:block transition-opacity duration-500"
            style={{
              background: 'radial-gradient(circle, rgba(100,140,255,0.07) 0%, transparent 70%)',
              left: -300,
              top: -300,
              opacity: 0,
            }}
          />

          {/* ── Global atmospheric background ── */}
          <div className="fixed inset-0 pointer-events-none z-0">
            {/* Ambient aurora glows — gives the black canvas depth across every section */}
            <div className="absolute top-[-10%] left-[-10%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full opacity-[0.09] blur-[120px]"
              style={{ background: 'radial-gradient(circle, rgba(100,140,255,0.5) 0%, transparent 70%)' }}
            />
            <div className="absolute bottom-[-15%] right-[-10%] w-[55vw] h-[55vw] max-w-[800px] max-h-[800px] rounded-full opacity-[0.07] blur-[120px]"
              style={{ background: 'radial-gradient(circle, rgba(140,100,255,0.5) 0%, transparent 70%)' }}
            />
            <div className="absolute inset-0 opacity-[0.02]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat',
                backgroundSize: '256px',
              }}
            />
          </div>

          <RouteFieldGate />

          <Navbar />

          <div id="main-content">
            <Suspense fallback={<SectionFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/travel-intelligence" element={<TravelIntelligence />} />
                <Route path="/corporate-travel" element={<CorporateTravel />} />
                <Route path="/destinations/:slug" element={<DestinationPage />} />
                <Route path="/knowledge" element={<KnowledgeHub />} />
                <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/about" element={<About />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>

          <Footer />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
